import { Api } from 'api';
import { CellActions, CellSwitch } from '../../../../components/Table/helpers';
import { useMemo, useState } from 'react';
import {
  FilterSelect,
  SelectColumnFilter,
} from 'components/Table/helpers/SelectColumnFilter/SelectColumnFilter';
import {
  DefaultTableSort,
  doFilterData,
  doSort,
  FilterPlaceholders,
  FilterTypes,
  SortByNumberOptions,
  SortByTextOptions,
  SortType,
} from 'components/Table/helpers/tableColumnSort';

interface Props {
  onClick: (action: boolean, id: any) => void;
  isLoading: boolean;
  handleAction: (action: string, row: any) => void;
  searchInput?: string;
}

export enum ScheduledJobsActions {
  Disable = 'Disable',
  Enable = 'Enable',
}

export const useTable = ({
  onClick,
  isLoading,
  handleAction,
  searchInput,
}: Props) => {
  const [sort, setSort] = useState<any>(DefaultTableSort);
  const [filter, setFilter] = useState<any>({});
  const { data, refetch, isLoading: isJobsLoading } = Api.jobs.GetJobs();
  const initData = data?.data ? [...data.data].reverse() : [];

  const sortByText = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Text } });
  };

  const sortByNumber = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Number } });
  };

  const handleFilter = (key: string, value: any) => {
    if (value.label === FilterPlaceholders.All) {
      delete filter[key];
      setFilter({ ...filter });
    } else {
      setFilter({ ...filter, [key]: value });
    }
  };

  const tableData = useMemo(() => {
    let d = doFilterData(initData, filter);
    if (searchInput) {
      d = d.filter((item: any) => {
        return item.name.toLowerCase().indexOf(searchInput) >= 0;
      });
    }
    const key = Object.keys(sort)[0];

    return doSort(d, key, sort[key]);
  }, [data, sort, filter, searchInput]);

  const columns = [
    {
      Header: 'Name of Job',
      accessor: 'name',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortByTextOptions,
          value: sort['name'],
          onChange: (props: any) => sortByText('name', props),
        }),
    },
    {
      Header: 'Frequency',
      accessor: 'jobFrequency.name',
      Cell: (row: any) => <div className='text-left'>{row.value}</div>,
      hasFilter: true,
      Filter: (props: any) =>
        SelectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['jobFrequency.name'],
          initData,
          onChange: (props: any) => handleFilter('jobFrequency.name', props),
        }),
    },
    {
      Header: 'Rolling Period',
      accessor: 'numberOfQuartersBack',
      Cell: (row: any) => (
        <div className='text-left'>
          {row.value}{' '}
          {row.value ? `${row.value > 1 ? 'quarters' : 'quarter'}` : ''}
        </div>
      ),
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortByNumberOptions,
          value: sort['numberOfQuartersBack'],
          onChange: (props: any) => sortByNumber('numberOfQuartersBack', props),
        }),
    },
    {
      Header: 'Active',
      accessor: 'isDisabled',
      className: 'textCenter',
      Cell: (row: any) => (
        <CellSwitch
          disabled={isLoading}
          enabled={!row.row.original.isDisabled}
          onClick={(e: boolean) => onClick(e, row.row.original.id)}
        />
      ),
      hasFilter: true,
      Filter: (props: any) =>
        SelectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['isDisabled'],
          initData,
          onChange: (props: any) => handleFilter('isDisabled', props),
          options: [
            {
              value: FilterTypes.All,
              label: FilterTypes.All,
            },
            {
              value: false,
              label: 'Active',
            },
            {
              value: true,
              label: 'Inactive',
            },
          ],
        }),
    },
    {
      Header: 'Actions',
      className: 'textCenter',
      accessor: 'col6',
      Cell: (row: any) => <CellActions onClick={handleAction} row={row} />,
    },
  ];

  const table = {
    data: tableData,
    columns,
  };

  return {
    table,
    refetch,
    isJobsLoading,
  };
};
